import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useLive } from '@/lib/experience';
import { Clock, MapPin } from 'lucide-react';
import { PHOTOS, WHATSAPP_DISPLAY, HOURS, LOCATION, INSTAGRAM_URL, INSTAGRAM_HANDLE, whatsappLink } from '@/data/site';
import { RevealWords } from './fx/RevealWords';
import { Magnetic } from './fx/Magnetic';
import { Photo } from './fx/Photo';
import { burst } from './fx/Burst';
import { Bow, WhatsAppIcon, InstagramIcon, ArrowUpRight, Heart, Cloudy, Paw } from './brand/Shapes';

const MESSAGE = 'Olá! Vim pelo site Cãopanhia Pet e gostaria de mais informações sobre os serviços de banho para pets!';

export const Contact = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const photoY = useTransform(scrollYProgress, [0, 1], ['-10%', '10%']);
  const bowRotate = useTransform(scrollYProgress, [0, 1], [-30, 20]);
  const live = useLive(ref);

  return (
    <section id="contato" data-nav="contato" ref={ref} className="relative bg-pet-cream px-3 md:px-5 pb-3 md:pb-5">
      <div className="relative overflow-hidden rounded-[40px] md:rounded-[64px] bg-gradient-to-br from-pet-blue-deep via-pet-blue to-[#7DB5FF] text-white">
        {/* nuvens e coraçõezinhos */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <Cloudy className="absolute w-40 md:w-56 right-[-3rem] top-8 text-white/25" />
          <Cloudy className="absolute w-28 md:w-40 right-[40%] -bottom-6 text-white/20" />
          {[...Array(6)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute text-white/30 hidden sm:block"
              style={{ left: `${10 + i * 15}%`, top: `${15 + (i % 3) * 25}%`, width: 14 + (i % 3) * 8 }}
              animate={live ? { y: [0, -14, 0], rotate: [0, i % 2 ? 15 : -15, 0] } : { y: 0, rotate: 0 }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              {i % 2 ? <Heart className="w-full" /> : <Paw className="w-full" />}
            </motion.span>
          ))}
        </div>

        <div className="container-cp relative grid lg:grid-cols-12 gap-12 lg:gap-10 items-center py-20 md:py-28">
          <div className="lg:col-span-7">
            <p className="kicker kicker-ears bg-white text-pet-blue mb-6 -rotate-2">
              <Heart className="w-4 h-4 text-pet-pink-deep" /> Contato
            </p>
            <RevealWords
              className="font-display font-medium text-[clamp(2.6rem,7.5vw,5.4rem)] leading-[1.05]"
              parts={[{ text: 'Seu pet em', br: true }, { text: 'boas mãos!', className: 'text-white px-1 [background-image:linear-gradient(transparent_58%,#FF8A9B_58%,#FF8A9B_92%,transparent_92%)] rounded-md' }]}
            />
            <p className="mt-7 text-lg md:text-xl leading-relaxed font-bold text-white max-w-xl">
              Estamos prontos para cuidar do seu pet com todo o carinho que ele merece. Entre em contato e agende uma visita!
            </p>

            <Magnetic strength={0.25} className="mt-10 w-full sm:w-auto">
              <motion.a
                href={whatsappLink(MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="main-whatsapp-btn"
                onClick={(e) => burst(e, 'heart', 12)}
                whileHover={{ scale: 1.04, rotate: -1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                className="group w-full sm:w-auto inline-flex items-center gap-3 md:gap-5 rounded-full bg-white text-pet-ink pl-3 pr-5 md:pr-8 py-3 shadow-[0_6px_0_0_#C9DDFB,0_30px_60px_-20px_rgba(0,0,0,0.35)]"
              >
                <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-pet-whats text-white flex items-center justify-center shrink-0 transition-transform duration-500 group-hover:rotate-[-14deg]">
                  <WhatsAppIcon className="w-7 h-7 md:w-8 md:h-8" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-extrabold text-pet-muted">Iniciar conversa</span>
                  <span className="block font-display text-[1.4rem] sm:text-2xl md:text-3xl leading-tight whitespace-nowrap text-pet-blue">{WHATSAPP_DISPLAY}</span>
                </span>
                <ArrowUpRight className="w-6 h-6 shrink-0 text-pet-blue transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </motion.a>
            </Magnetic>

            <div className="mt-10 grid sm:grid-cols-3 gap-3">
              {[
                { label: 'Horário', value: HOURS, Icon: Clock, bg: 'bg-pet-peach', ink: 'text-[#8A4B2A]' },
                { label: 'Local', value: LOCATION, Icon: MapPin, bg: 'bg-pet-pink', ink: 'text-pet-pink-ink' },
                { label: 'Instagram', value: INSTAGRAM_HANDLE, Icon: InstagramIcon, bg: 'bg-pet-mint', ink: 'text-pet-mint-ink', href: INSTAGRAM_URL },
              ].map(({ label, value, Icon, bg, ink, href }, i) => {
                const Tag = href ? motion.a : motion.div;
                return (
                  <Tag
                    key={label}
                    {...(href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className={`rounded-[24px] ${bg} ${ink} p-4 shadow-[0_14px_30px_-20px_rgba(0,0,0,0.4)] block`}
                    style={{ rotate: [-2, 1.5, -1][i] }}
                    whileHover={{ rotate: 0, y: -4, scale: 1.03 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 12 }}
                  >
                    <span className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider opacity-80">
                      <Icon className="w-4 h-4" /> {label}
                    </span>
                    <span className="mt-1.5 block font-extrabold leading-snug break-words">{value}</span>
                  </Tag>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5 relative max-w-[420px] w-full mx-auto">
            <motion.div
              className="relative aspect-[3/4] rounded-t-full rounded-b-[48px] overflow-hidden ring-[8px] ring-white shadow-2xl cursor-pointer"
              data-cursor="Carinho 💗"
              whileHover={{ rotate: 2, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={(e) => burst(e, 'heart', 12)}
              transition={{ type: 'spring', stiffness: 300, damping: 14 }}
            >
              <motion.div className="absolute inset-[-12%]" style={{ y: reduce ? 0 : photoY }}>
                <Photo photo={PHOTOS.noColo} sizes="(min-width: 1024px) 420px, 90vw" className="w-full h-full object-cover object-[50%_40%]" />
              </motion.div>
            </motion.div>
            <motion.div className="absolute -top-5 -left-6 w-[34%]" style={{ rotate: reduce ? -12 : bowRotate }} aria-hidden="true">
              <Bow className="w-full text-pet-pink-deep drop-shadow-[0_14px_20px_rgba(0,0,0,0.25)]" />
            </motion.div>
            <motion.div
              className="absolute -bottom-5 -right-2 md:-right-6 rounded-[22px] bg-white text-pet-ink px-4 py-3 shadow-xl"
              animate={{ rotate: [4, -2, 4] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <p className="font-display text-lg leading-none">
                Carinho · <span className="text-pet-blue">Qualidade</span> · <span className="text-pet-pink-deep">Confiança</span>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
